import { ChangeEvent } from 'react';
import { Pagination, Skeleton, Stack, Text } from '@ltvco/refresh-lib/theme';
import { Question } from 'api/requests/communityBuzz/interfaces';
import { QuestionDetails } from './QuestionDetails';
import { useScreenSize } from '@ltvco/refresh-lib/utils';

interface QuestionsListContentProps {
  onOpenQADisplayModal: () => void;
  setSelectedQuestion: (question?: Question) => void;
  questions: Question[];
  isLoading: boolean;
  isError: boolean;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (event: ChangeEvent<unknown>, page: number) => void;
}

export const QuestionsListContent = ({
  onOpenQADisplayModal,
  setSelectedQuestion,
  questions,
  isLoading,
  isError,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
}: QuestionsListContentProps) => {
  const { isMobile } = useScreenSize();
  const questionsList = questions || [];
  const effectiveTotal = totalCount || questionsList.length;
  const isEmpty = !isLoading && !isError && effectiveTotal === 0;
  const showList = !isLoading && !isError && questionsList.length > 0;
  const showError = !isLoading && isError;
  const showPagination = effectiveTotal > pageSize;
  const pageCount =
    effectiveTotal > 0 ? Math.ceil(effectiveTotal / pageSize) : 0;

  if (isLoading) {
    const skeletonRows = Math.max(3, Math.min(pageSize || 5, 6));

    return (
      <Stack minHeight={600}>
        {Array.from({ length: skeletonRows }).map((_, index) => (
          <Stack key={`community-buzz-skeleton-${index}`} spacing={1} py={2}>
            <Skeleton variant="text" width="90%" height={36} />
            <Skeleton variant="text" width="42%" height={24} />
            <Skeleton variant="text" width="55%" height={24} />
          </Stack>
        ))}
      </Stack>
    );
  }

  return (
    <>
      {showList && (
        <>
          <Stack spacing={3} mt={2}>
            {questionsList.map((question) => (
              <QuestionDetails
                key={String(question.id)}
                question={question}
                onOpenQADisplayModal={onOpenQADisplayModal}
                setSelectedQuestion={setSelectedQuestion}
              />
            ))}
          </Stack>
          {showPagination && (
            <Stack mt={2}>
              <Pagination
                count={pageCount}
                page={currentPage}
                shape="circular"
                onChange={onPageChange}
                showFirstButton
                showLastButton
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 2}
                sx={{
                  '&.MuiPagination-root': {
                    display: 'flex',
                    justifyContent: 'center',
                  },
                }}
              />
            </Stack>
          )}
        </>
      )}
      {isEmpty && (
        <Text variant="h4" color="text.secondary" textAlign="center" my={3}>
          No questions found for this vehicle
        </Text>
      )}
      {showError && (
        <Text variant="h4" color="text.secondary" textAlign="center" my={3}>
          Oops, we could not load this section for this vehicle.
        </Text>
      )}
    </>
  );
};
